/**
 * Frontend Base64 and PDF Validation Utilities
 * Validates PDF integrity before sending to backend
 */

/**
 * Validates Base64 string format (frontend version)
 * @param {string} base64String 
 * @returns {object} { valid: boolean, error: string|null }
 */
export function validateBase64Format(base64String) {
  if (!base64String || typeof base64String !== 'string') {
    return { valid: false, error: 'Base64 string is empty or not a string' };
  }

  // Remove any whitespace
  const cleaned = base64String.trim().replace(/[\s\r\n]/g, '');

  // Base64 regex
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(cleaned)) {
    return { 
      valid: false, 
      error: 'Invalid Base64 characters detected' 
    };
  }

  // Check length is multiple of 4
  if (cleaned.length % 4 !== 0) {
    return { 
      valid: false, 
      error: `Base64 length (${cleaned.length}) is not multiple of 4` 
    };
  }

  return { valid: true, error: null };
}

/**
 * Sanitizes Base64 string (removes whitespace)
 * @param {string} base64String 
 * @returns {object} { sanitized: string|null, valid: boolean, error: string|null }
 */
export function sanitizeBase64(base64String) {
  if (!base64String) {
    return { sanitized: null, valid: false, error: 'Empty input' };
  }

  // Remove whitespace: spaces, tabs, newlines
  const sanitized = base64String.replace(/[\s\r\n\t]/g, '');
  
  const validation = validateBase64Format(sanitized);
  return { 
    sanitized: validation.valid ? sanitized : null, 
    valid: validation.valid,
    error: validation.valid ? null : validation.error
  };
}

/**
 * Validates that Base64 decodes to a PDF with valid structure
 * @param {string} base64String 
 * @returns {object} { valid: boolean, error: string|null, details: object }
 */
export async function validatePDFStructure(base64String) {
  // Step 1: Validate Base64 format
  const sanitized = sanitizeBase64(base64String);
  if (!sanitized.valid) {
    return { 
      valid: false, 
      error: `Invalid Base64: ${sanitized.error}`,
      details: {}
    };
  }

  // Step 2: Decode to binary and check PDF header
  try {
    // Convert Base64 to Uint8Array
    const binary = atob(sanitized.sanitized);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    // Check PDF magic bytes: %PDF
    if (bytes.length < 5) {
      return { 
        valid: false, 
        error: 'PDF too small (less than 5 bytes)',
        details: { size: bytes.length }
      };
    }

    const header = String.fromCharCode(...bytes.slice(0, 5));
    if (!header.startsWith('%PDF')) {
      return { 
        valid: false, 
        error: `Invalid PDF header: "${header}" (expected "%PDF")`,
        details: { 
          headerBytes: Array.from(bytes.slice(0, 10)).map(b => b.toString(16).padStart(2, '0')).join(' ')
        }
      };
    }

    // Optional: Check for EOF trailer (more lenient on frontend)
    const maxCheck = Math.min(100, bytes.length);
    const tail = String.fromCharCode(...bytes.slice(Math.max(0, bytes.length - maxCheck)));
    const hasEOF = tail.includes('%%EOF');

    return { 
      valid: true, 
      error: null,
      details: {
        size: bytes.length,
        version: header.substring(1, 5),
        hasEOF,
        validHeader: true
      }
    };
  } catch (err) {
    return { 
      valid: false, 
      error: `PDF validation error: ${err.message}`,
      details: {}
    };
  }
}

/**
 * Get file size from Base64 string (approximate)
 * @param {string} base64String 
 * @returns {number} size in bytes
 */
export function getBase64FileSize(base64String) {
  if (!base64String) return 0;
  const cleaned = base64String.replace(/[\s\r\n\t]/g, '');
  const padding = (cleaned.match(/=/g) || []).length;
  return Math.floor((cleaned.length * 3) / 4 - padding);
}

/**
 * Warns if PDF exceeds a size threshold
 * @param {string} base64String 
 * @param {number} maxSizeBytes - Maximum acceptable size (default 100MB)
 * @returns {object} { valid: boolean, warning: string|null, sizeBytes: number }
 */
export function checkPDFSize(base64String, maxSizeBytes = 100 * 1024 * 1024) {
  const sizeBytes = getBase64FileSize(base64String);
  
  if (sizeBytes > maxSizeBytes) {
    return { 
      valid: false, 
      warning: `PDF size (${(sizeBytes / 1024 / 1024).toFixed(2)}MB) exceeds limit (${(maxSizeBytes / 1024 / 1024).toFixed(2)}MB)`,
      sizeBytes
    };
  }

  if (sizeBytes > 10 * 1024 * 1024) { // 10MB warning
    return { 
      valid: true, 
      warning: `Large PDF (${(sizeBytes / 1024 / 1024).toFixed(2)}MB) - upload may be slow`,
      sizeBytes
    };
  }

  return { 
    valid: true, 
    warning: null,
    sizeBytes
  };
}

/**
 * Complete validation pipeline for frontend
 * @param {string} base64String 
 * @returns {object} { valid: boolean, error: string|null, warning: string|null, details: object }
 */
export async function validatePDFBase64(base64String) {
  // Step 1: Sanitize
  const sanitized = sanitizeBase64(base64String);
  if (!sanitized.valid) {
    return { 
      valid: false, 
      error: sanitized.error,
      warning: null,
      details: {}
    };
  }

  // Step 2: Check structure
  const structureCheck = await validatePDFStructure(sanitized.sanitized);
  if (!structureCheck.valid) {
    return { 
      valid: false, 
      error: structureCheck.error,
      warning: null,
      details: structureCheck.details
    };
  }

  // Step 3: Check size
  const sizeCheck = checkPDFSize(sanitized.sanitized);

  return { 
    valid: true, 
    error: null,
    warning: sizeCheck.warning,
    details: {
      ...structureCheck.details,
      sizeBytes: sizeCheck.sizeBytes
    }
  };
}

/**
 * Debug-friendly validation report
 * @param {string} fileName 
 * @param {string} base64String 
 * @param {object} validationResult 
 */
export function logValidationReport(fileName, base64String, validationResult) {
  console.log('\n===== PDF BASE64 VALIDATION REPORT =====');
  console.log(`File: ${fileName}`);
  console.log(`Base64 length: ${base64String?.length || 0} chars`);
  console.log(`Approx file size: ${(getBase64FileSize(base64String) / 1024).toFixed(2)} KB`);
  console.log(`Valid: ${validationResult.valid}`);
  if (validationResult.error) console.log(`❌ Error: ${validationResult.error}`);
  if (validationResult.warning) console.log(`⚠️  Warning: ${validationResult.warning}`);
  if (validationResult.details) console.log(`Details:`, validationResult.details);
  console.log('==========================================\n');
}
